/**
 * The thread panel beside its channel (2026-10-29; Discord's split view; a
 * pane of its own on a phone, docs/design/visual-overhaul.md §12): the
 * thread's header (name, archived and locked marks, Join, members, pins,
 * search, its menu, close) over its conversation, which is a ChannelView of
 * the thread's ID (history, composer, typing, read marks, as any channel).
 * Members, pins and search open in the panel over the conversation, with a
 * Back to it.
 */
import {
  ChannelType,
  Permissions,
  THREAD_AUTO_ARCHIVE_DURATIONS,
  type Channel,
  type GuildMember,
  type Message,
  type Snowflake,
  type ThreadMember,
} from '@neosocial/protocol';
import { pushBack } from '@neosocial/ui/back';
import { useLayout } from '@neosocial/ui/layout';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { failToast } from '../mod/actions';
import { useFlag } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { can, channelPermissions, displayName, matchesMember, unreadOf } from '../store/selectors';
import { useUi } from '../store/ui';
import { Avatar, Btn, Icon, IconBtn } from '../ui/kit';
import { openMenu, toast, type MenuItem } from '../ui/overlay';
import { PreviewRow } from '../hub/Search';
import { openProfile } from '../hub/ProfileCard';
import { threadsApi } from './api';
import {
  closeThread,
  deleteThread,
  joinThread,
  leaveThread,
  markThreadRead,
  renameThread,
  setArchived,
  setAutoArchive,
  setInvitable,
  setLocked,
  threadPerms,
} from './actions';

type Sub = 'members' | 'pins' | 'search' | null;

/** The header's name cut off by its width says itself in a tooltip (QA D5). */
function tipWhenCut(e: React.PointerEvent<HTMLElement>) {
  const n = e.currentTarget;
  if (n.scrollWidth > n.clientWidth + 1) n.setAttribute('data-tip', n.textContent ?? '');
  else n.removeAttribute('data-tip');
}

/** The thread's menu: what the server lets the signed-in user do there. */
export function threadMenuItems(
  t: (k: string, v?: Record<string, string | number>) => string,
  th: Channel,
  o: {
    manage: boolean;
    send: boolean;
    meId: Snowflake | undefined;
    unread: boolean;
    extra?: MenuItem[];
  },
): MenuItem[] {
  const meta = th.thread_metadata;
  const archived = !!meta?.archived;
  const locked = !!meta?.locked;
  const owner = !!o.meId && th.owner_id === o.meId;
  // the creator changes their own thread while it isn't locked; Manage Threads always
  const edits = o.manage || (owner && !locked);
  const items: MenuItem[] = [...(o.extra ?? [])];
  if (o.unread)
    items.push({ label: t('th.menu.markRead'), icon: 'check', run: () => markThreadRead(th) });
  if (!archived) {
    if (th.member)
      items.push({ label: t('th.menu.leave'), icon: 'arrow-out', run: () => void leaveThread(th) });
    else if (o.send && th.type === ChannelType.PUBLIC_THREAD)
      items.push({ label: t('th.menu.join'), icon: 'arrow-in', run: () => void joinThread(th) });
  }
  const changes: MenuItem[] = [];
  if (edits && !archived) {
    changes.push(
      { label: t('th.menu.rename'), icon: 'edit', run: () => void renameThread(th) },
      {
        label: t('th.menu.hideAfter'),
        icon: 'clock',
        submenu: () =>
          THREAD_AUTO_ARCHIVE_DURATIONS.map((m) => ({
            label: t(`th.archive.${m}`),
            checked: meta?.auto_archive_duration === m,
            run: () => void setAutoArchive(th, m),
          })),
      },
    );
  }
  if (edits)
    changes.push({
      label: t(archived ? 'th.menu.unarchive' : 'th.menu.archive'),
      icon: 'history',
      run: () => void setArchived(th, !archived),
    });
  if (o.manage) {
    changes.push({
      label: t(locked ? 'th.menu.unlock' : 'th.menu.lock'),
      icon: 'lock',
      run: () => void setLocked(th, !locked),
    });
    if (th.type === ChannelType.PRIVATE_THREAD && !archived)
      changes.push({
        label: t('th.menu.invitable'),
        icon: 'user-plus',
        checked: meta?.invitable !== false,
        toggle: true,
        run: () => void setInvitable(th, meta?.invitable === false),
      });
  }
  if (changes.length) {
    if (items.length) items.push({ sep: true });
    items.push(...changes);
  }
  if (o.manage) {
    items.push(
      { sep: true },
      { label: t('th.menu.delete'), icon: 'trash', danger: true, run: () => void deleteThread(th) },
    );
  }
  return items;
}

export function ThreadPanel({ threadId, children }: { threadId: Snowflake; children: ReactNode }) {
  const { t } = useT();
  const th = useData((d) => d.channels[threadId]);
  const p = useDataShallow((d) => threadPerms(d, th));
  const unread = useDataShallow((d) => unreadOf(d, threadId));
  const meId = useData((d) => d.me?.id);
  const lay = useLayout();
  const phone = lay.layout === 'phone';
  // a phone, a tablet or a touch screen: 44px targets beside Join, members,
  // pins, search, the menu and close left the name 2px wide (QA D5), and a
  // phone keeps at most three actions (§12.7 A1): pins and search go in the menu
  const fold = phone || lay.layout === 'tablet' || lay.pointer === 'coarse';
  const pinsOn = useFlag('pins');
  const searchOn = useFlag('search');
  const [sub, setSub] = useState<Sub>(null);
  // another thread: its own conversation first
  const [shown, setShown] = useState(threadId);
  if (shown !== threadId) {
    setShown(threadId);
    setSub(null);
  }
  // a phone: members, pins or search is a back-stack layer (the pane's Back closes it first)
  useEffect(() => {
    if (!sub || !phone) return;
    return pushBack(() => setSub(null));
  }, [sub, phone]);
  if (!th) return null;
  const meta = th.thread_metadata;
  const isPrivate = th.type === ChannelType.PRIVATE_THREAD;
  const toggle = (s: Exclude<Sub, null>) => setSub((x) => (x === s ? null : s));
  const folded: MenuItem[] = [];
  if (pinsOn) folded.push({ label: t('th.panel.pins'), icon: 'pin', run: () => setSub('pins') });
  if (searchOn)
    folded.push({ label: t('th.panel.search'), icon: 'search', run: () => setSub('search') });
  const more = (anchor: Element) =>
    openMenu(
      anchor,
      threadMenuItems(t, th, {
        manage: p.manage,
        send: p.send,
        meId,
        unread: unread.unread || unread.mentions > 0,
        // a narrow header has room for fewer buttons: pins and search move here
        extra: fold ? folded : [],
      }),
      { placement: 'bottom-end' },
    );
  const jumpTo = (m: Message) => {
    setSub(null);
    useUi.getState().set({ jump: { channelId: threadId, messageId: m.id } });
  };
  const title =
    sub === 'members'
      ? t('th.panel.members')
      : sub === 'pins'
        ? t('th.panel.pins')
        : sub === 'search'
          ? t('th.panel.search')
          : (th.name ?? '');
  return (
    <div
      className="ns-thread slot-panel"
      role="region"
      aria-label={t('th.panel.label', { name: th.name ?? '' })}
      data-archived={meta?.archived || undefined}
    >
      <header className="slot-h ns-th-h">
        {/* a phone's pane Back sits here (and closes the view over the thread first) */}
        {phone ? null : sub ? (
          <IconBtn icon="chev-left" label={t('th.panel.back')} onClick={() => setSub(null)} />
        ) : (
          <span className="ns-th-h-i" aria-hidden="true">
            <Icon name={isPrivate ? 'lock' : 'thread'} size="m" />
          </span>
        )}
        <h2 className="slot-t" dir={sub ? undefined : 'auto'} onPointerEnter={tipWhenCut}>
          {title}
        </h2>
        {!sub && meta?.locked ? (
          <span className="ns-th-chip" data-tip={t('th.locked.note')}>
            <Icon name="lock" size="s" />
            {t('th.row.locked')}
          </span>
        ) : null}
        {!sub && meta?.archived ? <span className="ns-th-chip">{t('th.row.archived')}</span> : null}
        {!sub && !th.member && !isPrivate && p.send && !meta?.archived ? (
          <Btn
            kind="secondary"
            size="xs"
            className="ns-th-join"
            onClick={() => void joinThread(th)}
          >
            {t('th.panel.join')}
          </Btn>
        ) : null}
        <IconBtn
          icon="users"
          label={t('th.panel.members')}
          tipPos="bottom"
          pressed={sub === 'members'}
          onClick={() => toggle('members')}
        />
        {pinsOn && !fold ? (
          <IconBtn
            icon="pin"
            label={t('th.panel.pins')}
            tipPos="bottom"
            pressed={sub === 'pins'}
            onClick={() => toggle('pins')}
          />
        ) : null}
        {searchOn && !fold ? (
          <IconBtn
            icon="search"
            label={t('th.panel.search')}
            tipPos="bottom"
            pressed={sub === 'search'}
            onClick={() => toggle('search')}
          />
        ) : null}
        <IconBtn
          icon="more"
          label={t('th.panel.more')}
          tipPos="bottom"
          aria-haspopup="menu"
          onClick={(e) => more(e.currentTarget)}
        />
        {/* a phone's frame Back closes the thread */}
        {!phone ? (
          <IconBtn
            icon="x"
            label={t('th.panel.close')}
            tipPos="bottom"
            onClick={() => closeThread(th)}
          />
        ) : null}
      </header>
      {sub === 'members' ? <ThreadMembers thread={th} manage={p.manage} send={p.send} /> : null}
      {sub === 'pins' ? <ThreadPins threadId={threadId} onOpen={jumpTo} /> : null}
      {sub === 'search' ? <ThreadSearch threadId={threadId} onOpen={jumpTo} /> : null}
      {/* the conversation stays mounted under the other views: its place is kept */}
      <div className="ns-th-chat" hidden={!!sub}>
        {children}
      </div>
    </div>
  );
}

/* --------------------------------- members -------------------------------- */

function ThreadMembers({
  thread,
  manage,
  send,
}: {
  thread: Channel;
  manage: boolean;
  send: boolean;
}) {
  const { t } = useT();
  const gid = thread.guild_id ?? '';
  const meId = useData((d) => d.me?.id);
  const [list, setList] = useState<ThreadMember[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const count = thread.member_count ?? 0;
  useEffect(() => {
    let live = true;
    threadsApi
      .members(thread.id)
      .then((r) => live && (setList(r), setError(false)))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [thread.id, count]);
  const isPrivate = thread.type === ChannelType.PRIVATE_THREAD;
  const owner = !!meId && thread.owner_id === meId;
  const archived = !!thread.thread_metadata?.archived;
  // as the server checks: Send Messages in Threads, and in a private one membership and invitable (or Manage Threads)
  const canAdd =
    !archived &&
    send &&
    (!isPrivate || manage || (!!thread.member && thread.thread_metadata?.invitable !== false));
  const canRemove = !archived && (manage || (isPrivate && owner));
  const inThread = useMemo(() => new Set((list ?? []).map((m) => m.user_id)), [list]);
  const candidates = useDataShallow((d) => {
    if (!canAdd || !q.trim()) return [] as GuildMember[];
    const parent = thread.parent_id ? d.channels[thread.parent_id] : undefined;
    return Object.values(d.members[gid] ?? {})
      .filter(
        (m) =>
          !inThread.has(m.user.id) &&
          matchesMember(m.user, m, q) &&
          can(channelPermissions(d, parent, m.user.id), Permissions.VIEW_CHANNEL),
      )
      .slice(0, 8);
  });
  const add = (m: GuildMember) =>
    void threadsApi
      .add(thread.id, m.user.id)
      .then(() => {
        setQ('');
        toast(t('th.mem.added'), { icon: 'user-plus' });
      })
      .catch(failToast);
  const remove = (uid: Snowflake) =>
    void threadsApi
      .remove(thread.id, uid)
      .then(() => toast(t('th.mem.removed'), { icon: 'arrow-out' }))
      .catch(failToast);
  return (
    <div className="ns-th-sub nx-scroll" role="region" aria-label={t('th.panel.members')}>
      {canAdd ? (
        <label className="ns-search-in ns-th-add">
          <Icon name="user-plus" size="s" />
          <input
            type="search"
            dir="auto"
            value={q}
            placeholder={t('th.mem.addPh')}
            aria-label={t('th.mem.add')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      ) : null}
      {q.trim() && canAdd ? (
        candidates.length ? (
          <ul className="ns-th-mems" aria-label={t('th.mem.add')}>
            {candidates.map((m) => (
              <li key={m.user.id}>
                <button type="button" className="ns-th-mem" onClick={() => add(m)}>
                  <Avatar user={m.user} name={displayName(m.user, m)} size={32} />
                  <span className="ns-th-mem-n" dir="auto">
                    {displayName(m.user, m)}
                  </span>
                  <Icon name="plus" size="s" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ns-muted slot-note">{t('th.mem.noMatch')}</p>
        )
      ) : null}
      {error ? <p className="ns-muted slot-note">{t('th.mem.error')}</p> : null}
      {!list && !error ? (
        <p className="ns-muted slot-note">
          <i className="nx-spin" /> {t('ns.loading')}
        </p>
      ) : null}
      {list && !list.length ? <p className="ns-muted slot-note">{t('th.mem.none')}</p> : null}
      {list?.length ? (
        <ul className="ns-th-mems" aria-label={t('th.mem.title')}>
          {list.map((m) => (
            <MemberRow
              key={m.user_id}
              m={m}
              guildId={gid}
              onRemove={canRemove && m.user_id !== meId ? () => remove(m.user_id!) : undefined}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function MemberRow({
  m,
  guildId,
  onRemove,
}: {
  m: ThreadMember;
  guildId: Snowflake;
  onRemove?: () => void;
}) {
  const { t } = useT();
  const uid = m.user_id ?? '';
  const user = useData((d) => d.users[uid] ?? m.member?.user);
  const gm = useData((d) => d.members[guildId]?.[uid] ?? m.member);
  const name = user ? displayName(user, gm) : '';
  return (
    <li className="ns-th-mem-li">
      <button
        type="button"
        className="ns-th-mem"
        onClick={(e) => openProfile(uid, e.currentTarget, 'start-start')}
      >
        <Avatar user={user} name={name} size={32} />
        <span className="ns-th-mem-n" dir="auto">
          {name}
        </span>
      </button>
      {onRemove ? (
        <IconBtn icon="x" size="xs" label={t('th.mem.remove')} onClick={onRemove} />
      ) : null}
    </li>
  );
}

/* ---------------------------------- pins ---------------------------------- */

function ThreadPins({ threadId, onOpen }: { threadId: Snowflake; onOpen(m: Message): void }) {
  const { t } = useT();
  const version = useData((d) => d.pinsVersion[threadId] ?? 0);
  const [items, setItems] = useState<Message[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    api.channels
      .pins(threadId)
      .then((r) => live && (setItems(r.items.map((x) => x.message)), setError(false)))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [threadId, version]);
  return (
    <div className="ns-th-sub nx-scroll" role="region" aria-label={t('th.panel.pins')}>
      {error ? <p className="ns-muted slot-note">{t('ns.pins.error')}</p> : null}
      {!items && !error ? (
        <p className="ns-muted slot-note">
          <i className="nx-spin" /> {t('ns.loading')}
        </p>
      ) : null}
      {items && !items.length ? (
        <div className="slot-empty">
          <span className="slot-empty-ic" aria-hidden="true">
            <Icon name="pin" size="xl" />
          </span>
          <p>{t('th.pins.none')}</p>
        </div>
      ) : null}
      {items?.length ? (
        <ul className="ns-pins-list">
          {items.map((m) => (
            <PreviewRow
              key={m.id}
              m={m}
              cls="ns-pin"
              onOpen={() => onOpen(m)}
              close={() => undefined}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* --------------------------------- search --------------------------------- */

function ThreadSearch({ threadId, onOpen }: { threadId: Snowflake; onOpen(m: Message): void }) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [res, setRes] = useState<{ key: string; total: number; messages: Message[] } | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => {
    if (!debounced) return;
    let live = true;
    threadsApi
      .search(threadId, debounced)
      .then(
        (r) =>
          live &&
          (setRes({ key: debounced, total: r.total_results, messages: r.messages }),
          setError(false)),
      )
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [debounced, threadId]);
  const shown = res && res.key === debounced ? res : null;
  return (
    <div className="ns-th-sub nx-scroll" role="region" aria-label={t('th.panel.search')}>
      <label className="ns-search-in ns-th-add">
        <Icon name="search" size="s" />
        <input
          type="search"
          dir="auto"
          value={q}
          placeholder={t('th.search.ph')}
          aria-label={t('th.search.ph')}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
      </label>
      {error ? <p className="ns-muted slot-note">{t('ns.err.generic')}</p> : null}
      {debounced && !shown && !error ? (
        <p className="ns-muted slot-note">
          <i className="nx-spin" /> {t('ns.loading')}
        </p>
      ) : null}
      {shown ? (
        <p className="ns-muted slot-note nx-num">{t('th.search.results', { n: shown.total })}</p>
      ) : null}
      {shown && !shown.messages.length ? (
        <p className="ns-muted slot-note">{t('th.search.none')}</p>
      ) : null}
      {shown?.messages.length ? (
        <ul className="ns-search-list">
          {shown.messages.map((m) => (
            <PreviewRow key={m.id} m={m} onOpen={() => onOpen(m)} close={() => undefined} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
